<template>
  <svg :width="`${widthInInches}in`" :height="`${heightInInches}in`" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <!-- Define a mask using the PNG -->
      <mask v-for="(artwork, index) in frameSpec.artworks" :key="index" :id="`${uuid}-pngMask`">
        <!-- Use the PNG image to define the mask -->
        <image
          :x="artworkOffsetX + (fullMat && fullMat.openings ? fullMat.openings[index].leftInInches : 0)"
          :y="artworkOffsetY + (fullMat && fullMat.openings ? fullMat.openings[index].topInInches : 0)"
          :width="artworkWidthInInches"
          :height="artworkHeightInInches"
          preserveAspectRatio="none"
          :href="imageUrl"
        />
      </mask>

      <!-- Define a filter for the drop shadow -->
      <filter :id="`${uuid}-dropShadow`" x="0" y="0" :width="widthInInches" :height="heightInInches" filterUnits="userSpaceOnUse">
        <feGaussianBlur in="SourceAlpha" stdDeviation="0.1" />
        <feOffset dx="-0.1" dy="0.1" result="offsetblur" />
        <feMerge>
          <feMergeNode in="offsetblur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <!-- Backing board component -->
    <RenderSvgBackingBoard
      :frameSpec="frameSpec"
      :widthInInches="widthInInches"
      :heightInInches="heightInInches"
      :x="0"
      :y="0"
    />
    

    <!-- Apply the mask and drop shadow to the shape -->
    <g v-if="floatedMount" :style="`filter: url(#${uuid}-dropShadow)`">
      <rect
        v-if="imageIsPng"
        :x="artworkOffsetX"
        :y="artworkOffsetY"
        :width="artworkWidthInInches"
        :height="artworkHeightInInches"
        :mask="`url(#${uuid}-pngMask)`"
        fill="rgb(0, 0, 0, 0.5)"
      />
      <rect
        v-else
        :x="artworkOffsetX"
        :y="artworkOffsetY"
        :width="artworkWidthInInches"
        :height="artworkHeightInInches"
        fill="rgb(0, 0, 0, 0.5)"
      />
    </g>

    <!-- Artwork component -->
    <RenderSvgArtwork
      v-for="(artwork, artworkKey) in frameSpec.artworks"
      :key="artworkKey"
      :artworkKey="artworkKey"
      :frameSpec="frameSpec"
      :moulding="moulding"
      :x="artworkOffsetX + (fullMat && fullMat.openings ? fullMat.openings[artworkKey].leftInInches : 0)"
      :y="artworkOffsetY + (fullMat && fullMat.openings ? fullMat.openings[artworkKey].topInInches : 0)"
      :allowUpload="allowUpload"
      :artworkUrl="artworkUrls[artworkKey]"
      :loading="loading[artworkKey]"
      :readonly="readonly"
      @upload-click="$emit('upload-click')"
      @click="onArtworkClick($event, artworkKey)"
      @drop.stop="emitArtworkDrop($event, artworkKey)"
    />

    <!-- Mat components -->
    <RenderSvgMat
      v-for="(mat, index) in frameSpec.mats.slice().reverse()"
      :key="index"
      :frameSpec="frameSpec"
      :x="0"
      :y="0"
      :mat="mat"
      :matIndex="index"
    />
  </svg>
</template>

<script>
import RenderSvgArtwork from './RenderSvgArtwork.vue';
import RenderSvgMat from './RenderSvgMat.vue';
import RenderSvgBackingBoard from './RenderSvgBackingBoard.vue';
import { uploadedArtworkMatOverlap } from "../../hardCodedValues";
import isEmpty from "lodash/isEmpty";
import uuidv4 from "uuid/v4";

export default {
  components: {
    RenderSvgArtwork,
    RenderSvgMat,
    RenderSvgBackingBoard
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    allowUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    artworkPatterns: {
      type: Array,
      required: false,
      default: []
    },
    artworkUrl: {
      type: String,
      required: false
    },
    loading: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    inBundle: {
      type: Object,
      required: false
    },
    moulding: {
      type: Object,
      required: false
    },
  },
  inject: ['emitArtworkClick', 'emitArtworkDrop'],
  computed: {
    isMultiArtwork() {
      return this.frameSpec.artworks.length > 1;
    },
    fullMat () {
      return this.frameSpec.getFullPrimaryMat();
    },
    uuid() {
      return uuidv4();
    },
    artwork() {
      return this.frameSpec.getArtwork() || {};
    },
    floatedMount() {
      return this.frameSpec.isFloated();
    },
    mat() {
      return this.frameSpec.mats[0] || {};
    },
    matImage () {
      return this.fullMat && this.fullMat.matAsset ? this.fullMat.matAsset : null;
    },
    imageIsPng() {
      if (!this.frameSpec) return "";

      const customerSpecifiedHasPng = this.artwork.customerSpecified && this.artwork.customerSpecified.url.includes(".png");
      const originalUneditedHasPng = this.artwork.originalUnedited && this.artwork.originalUnedited.url.includes(".png");
      return customerSpecifiedHasPng || originalUneditedHasPng;
    },
    imageUrl() {
      if (!this.frameSpec) return "";

      const croppedAndNormalizedUrl = this.artwork.croppedAndNormalized && this.artwork.croppedAndNormalized.url;
      const customerSpecifiedUrl = this.artwork.customerSpecified && this.artwork.customerSpecified.url;

      // Digital PNGs print on white paper, so use the opaque JPEG (customerSpecified)
      // rather than the transparent croppedAndNormalized PNG.
      if (this.imageIsPng && this.frameSpec.isDigital()) {
        return customerSpecifiedUrl || croppedAndNormalizedUrl;
      }

      if (this.imageIsPng || !customerSpecifiedUrl) {
        return croppedAndNormalizedUrl;
      }

      return customerSpecifiedUrl;
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvasOrCanvasPaper();
    },
    totalsOfAllMatRevealsOrMountReveals() {
      if (!isEmpty(this.mat)) {
        return {
          topInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
          rightInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
          bottomInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
          leftInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
        };
      } else if (this.frameSpec.isFloated() || this.frameSpec.isClearFloated()) {
        return this.frameSpec.mount.reveal;
      } else {
        return {
          topInInches: 0,
          rightInInches: 0,
          bottomInInches: 0,
          leftInInches: 0
        };
      }
    },
    widthInInches() {
      if (this.fullMat && this.fullMat.matWidth) {
        return this.fullMat.matWidth;
      }
      if (!this.artwork.exterior) return 0;
      return this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsOrMountReveals.leftInInches + this.totalsOfAllMatRevealsOrMountReveals.rightInInches;
    },
    heightInInches() {
      if (this.fullMat && this.fullMat.matHeight) {
        return this.fullMat.matHeight;
      }
      if (!this.artwork.exterior) return 0;
      return this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsOrMountReveals.topInInches + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches;
    },
    artworkWidthInInches() {
      return this.artwork.exterior.widthInInches;
    },
    artworkHeightInInches() {
      return this.artwork.exterior.heightInInches;
    },
    artworkOffsetX() {
      if (this.isCanvasMoulding || this.matImage || !this.frameSpec.shouldHaveMatOverlappingArtwork()) {
        return this.totalsOfAllMatRevealsOrMountReveals.leftInInches;
      } else {
        return this.totalsOfAllMatRevealsOrMountReveals.leftInInches - (uploadedArtworkMatOverlap / 2);
      } 
    },
    artworkOffsetY() {
      if (this.isCanvasMoulding || this.matImage || !this.frameSpec.shouldHaveMatOverlappingArtwork()) {
        return this.totalsOfAllMatRevealsOrMountReveals.topInInches;
      } else {
        return this.totalsOfAllMatRevealsOrMountReveals.topInInches - (uploadedArtworkMatOverlap / 2);
      }  
    },
    artworkUrls() {
      return this.frameSpec.artworks.map((artwork, artworkKey) => {
        if (this.artworkPatterns[artworkKey] && this.artworkPatterns[artworkKey].pattern && this.artworkPatterns[artworkKey].pattern.url) {
          return this.artworkPatterns[artworkKey].pattern.url;
        }
      });
    }
  },
  methods: {
    onArtworkClick(ev, artworkKey) {
      if (this.inBundle) {
        ev.stopPropagation();
      }

      this.emitArtworkClick(artworkKey);
    }
  }
};
</script>

<style lang="scss" scoped>
svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  width: 100%;
}
</style>